import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { SaleType, SaleTypeMap } from 'features/common/models/saletype';
import moment from 'moment';
import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt } from 'react-icons/fa';
import Select from 'react-select';
import { DATE_FORMAT_YYYY_MM_DD, displayError } from 'utils';
import { handleServiceError } from 'utils/axiosError';
import CourseDetailModal from '../components/CourseDetailModal';
import MenuBookDetailModal from '../components/MenuBookDetailModal';
import MenuBookEditorModal from '../components/MenuBookEditorModal';
import MenuBookListTable from '../components/MenuBookListTable';
import { menuBookEditorActions } from '../menuBookEditorSlice';
import menuBookListService from '../menuBookListService';
import menuBookService from '../menuBookService';
import { RestaurantOption } from '../models';
import { MenuBookDeletePayload, MenuBookListItem, MenuBookListRequestPayload } from '../models/menuBook';

const SEARCH_SHOW_DELETE_KEY_DEFAULT = '0';
const SEARCH_TAB_KEY_1 = 'sale-type';
const SEARCH_TAB_KEY_2 = 'store';

interface FormSearchValueProps {
  menuBookName: string;
  saleType: string;
  showFlg: '0' | '1' | '2';
  restaurantOption: RestaurantOption | null;
}

const CustomInput = React.forwardRef((props: any, ref: any) => {
  return (
    <div style={{ position: 'relative' }}>
      <FaCalendarAlt style={{ position: 'absolute', top: '8px', left: '10px' }} onClick={props.onClick} />
      <input
        className="form-control"
        onClick={props.onClick}
        style={{ paddingLeft: '32px' }}
        value={props.value}
        onChange={props.onChange}
        type="text"
      />
    </div>
  );
});

function MenuBookListPage() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();

  // states
  const [showEditMenuBookModal, setShowEditMenuBookModal] = useState(false);
  const [showMenuBookDetailModal, setShowMenuBookDetailModal] = useState(false);
  const [showCourseDetailModal, setShowCourseDetailModal] = useState(false);
  const [searchDetailTabKey, setSearchDetailTabKey] = useState<string | null>(SEARCH_TAB_KEY_1);
  const [dataDate, setDataDate] = useState(new Date());
  const [searchRestaurantOption, setSearchRestaurantOption] = useState<RestaurantOption | null>(null);

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //ref
  const btnViewDetailRef = useRef<HTMLButtonElement>(null);
  const btnAddCourseRef = useRef<HTMLButtonElement>(null);
  const btnCopyRef = useRef<HTMLButtonElement>(null);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);

  const sortMenuBookRef = useRef<string>('priority');
  const sortCourseRef = useRef<string>('priority');

  const [selectedMenuBookId, setSelectedMenuBookId] = useState<number>();
  const [courseDetailModalData, setCourseDetailModalData] = useState<{
    menuBookId: number;
    menuBookName: string;
    courseId?: number;
    copyFromCourseId?: number;
  }>();

  // MenuBookEditorModal data
  const [menuBookEditorData, setMenuBookEditorData] = useState<{
    menuBookId?: number;
    menuBookName?: string;
    courseId?: number;
    courseName?: string;
  }>();

  // copy
  const [copyFromMenuBookId, setCopyFromMenuBookId] = useState<number>();

  const [saleTypeSourceMap, setSaleTypeSourceMap] = useState<SaleTypeMap>();
  const [restaurantOptionListSource, setRestaurantOptionListSource] = useState<RestaurantOption[]>([]);
  const [menuBookList, setMenuBookList] = useState<MenuBookListItem[]>([]);

  const formSearchRef = useRef<HTMLFormElement>(null);
  const [formSearchValue, setFormSearchValue] = useState<FormSearchValueProps>({
    menuBookName: '',
    saleType: '',
    showFlg: '1',
    restaurantOption: null,
  });

  const SHOW_STATUS_SOURCE: { [key: string]: string } = {
    '0': t('MenuBookListPage.show_valid'),
    '1': t('MenuBookListPage.show_all'),
    '2': t('MenuBookListPage.show_deleted'),
  };

  function loadRestaurantOptionListSource(date: Date) {
    (async () => {
      try {
        const response = await menuBookListService.getRestaurantList(date);
        setRestaurantOptionListSource(response.data);
        setSearchRestaurantOption(null);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadSaleTypeDataSource() {
    (async () => {
      try {
        const response = await masterDataService.getSaleType();
        const saleTypeMap = response.data.sale_type.reduce((saleTypeMap: SaleTypeMap, saleType: SaleType) => {
          saleTypeMap[saleType.id] = saleType.name;
          return saleTypeMap;
        }, {});
        setSaleTypeSourceMap(saleTypeMap);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  const handleSortMenuBook = useCallback(() => {
    const order = sortMenuBookRef.current === 'priority' ? '-priority' : 'priority';
    sortMenuBookRef.current = order;
    loadMenuListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortCourse = useCallback(() => {
    const order = sortCourseRef.current === 'priority' ? '-priority' : 'priority';
    sortCourseRef.current = order;
    loadMenuListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle status button actions
  useEffect(() => {
    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnAddCourseRef.current) btnAddCourseRef.current.disabled = true;
    if (btnCopyRef.current) btnCopyRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }, []);

  useEffect(() => {
    loadSaleTypeDataSource();
    loadRestaurantOptionListSource(dataDate);
    loadMenuListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadMenuListData() {
    if (formSearchRef.current) {
      const name = formSearchRef.current['menu_book_name'].value.trim() as string;
      const showFlg = Number(formSearchRef.current['show_flg'].value.trim());
      const saleTypeId = formSearchRef.current['sale_type_id'].value.trim() as string;
      const restaurantId = searchRestaurantOption?.value;
      (async () => {
        try {
          const params: MenuBookListRequestPayload = {
            display_type: 1,
            name: name.length > 0 ? name : undefined,
            show_flg: showFlg,
            ordering: `${sortMenuBookRef.current},${sortCourseRef.current}`,
          };
          if (searchDetailTabKey === SEARCH_TAB_KEY_1) {
            params.sale_type_id = saleTypeId.length > 0 ? Number(saleTypeId) : undefined;
          } else {
            params.restaurant_id = restaurantId ? Number(restaurantId) : undefined;
          }

          const response = await menuBookListService.getMenuBookList(params);
          setMenuBookList(response.data);
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    handleUnSelectAll();
    loadMenuListData();
  }

  function saveFormSearch() {
    if (formSearchRef.current) {
      setFormSearchValue({
        ...formSearchValue,
        menuBookName: formSearchRef.current['menu_book_name'].value.trim(),
        saleType: formSearchRef.current['sale_type_id'].value.trim(),
        showFlg: formSearchRef.current['show_flg'].value.trim(),
        restaurantOption: searchRestaurantOption,
      });
    }
  }

  function handleSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-menubook-item > input');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = true;
    });

    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnAddCourseRef.current) btnAddCourseRef.current.disabled = true;
    if (btnCopyRef.current) btnCopyRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = false;
  }

  function handleUnSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-menubook-item > input');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = false;
    });

    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnAddCourseRef.current) btnAddCourseRef.current.disabled = true;
    if (btnCopyRef.current) btnCopyRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }

  const handleOpenEditMenuBookModal = useCallback(function ({
    menuBookId,
    menuBookName,
    courseId,
    courseName,
  }: {
    menuBookId?: number;
    menuBookName?: string;
    courseId?: number;
    courseName?: string;
  }) {
    setMenuBookEditorData({
      menuBookId,
      menuBookName,
      courseId,
      courseName,
    });
    setShowEditMenuBookModal(true);
  },
  []);

  function handleCloseEditMenuBookModal() {
    setShowEditMenuBookModal(false);
    setMenuBookEditorData(undefined);
    dispatch(menuBookEditorActions.resetData());
  }

  function handleOpenMenuBookDetailModal() {
    setShowMenuBookDetailModal(true);
  }

  function handleCloseMenuBookDetailModal(isDataChanged: boolean) {
    handleUnSelectAll();
    setShowMenuBookDetailModal(false);
    setSelectedMenuBookId(undefined);
    setCopyFromMenuBookId(undefined);

    if (isDataChanged) {
      loadMenuListData();
    }
  }

  function handleOpenCourseDetailModal() {
    const chkSelectionElems = document.querySelectorAll('.chk-menubook-item > input:checked');
    if (chkSelectionElems.length > 0) {
      if (chkSelectionElems.length > 1) {
        return;
      }

      const selectedElementChk = chkSelectionElems[0] as HTMLInputElement;

      if (selectedElementChk.dataset.type === 'menubook') {
        const selectedMenuBookId = Number(selectedElementChk.value);
        setCourseDetailModalData({
          ...courseDetailModalData,
          menuBookId: selectedMenuBookId,
          menuBookName: selectedElementChk.dataset.menubookname || '',
        });
        setShowCourseDetailModal(true);
      }
    }
  }

  function handleCloseCourseDetailModal(isDataChanged: boolean) {
    handleUnSelectAll();
    setShowCourseDetailModal(false);
    setCourseDetailModalData(undefined);

    if (isDataChanged) {
      loadMenuListData();
    }
  }

  function handleDateChanged(newDate: Date) {
    setDataDate(newDate);
    loadRestaurantOptionListSource(newDate);
  }

  function handleSelectDepartmentChange(option: RestaurantOption | null) {
    setSearchRestaurantOption(option);
  }

  function handleOpenMenuBookOrCourseDetailModal() {
    const chkSelectionElems = document.querySelectorAll('.chk-menubook-item > input:checked');
    if (chkSelectionElems.length > 0) {
      if (chkSelectionElems.length > 1) {
        return;
      }

      const selectedElementChk = chkSelectionElems[0] as HTMLInputElement;

      if (selectedElementChk.dataset.type === 'menubook') {
        const menuBookId = Number(selectedElementChk.value);
        setSelectedMenuBookId(menuBookId);
        setCopyFromMenuBookId(undefined);
        setShowMenuBookDetailModal(true);
      } else if (selectedElementChk.dataset.type === 'course') {
        const [menuBookId, courseId] = selectedElementChk.value.split('-');
        setCourseDetailModalData({
          ...courseDetailModalData,
          menuBookId: Number(menuBookId),
          menuBookName: selectedElementChk.dataset.menubookname || '',
          courseId: Number(courseId),
          copyFromCourseId: undefined,
        });
        setShowCourseDetailModal(true);
      }
    }
  }

  function handleCopyMenuBookOrCourseDetailModal() {
    const chkSelectionElems = document.querySelectorAll('.chk-menubook-item > input:checked');
    if (chkSelectionElems.length > 0) {
      if (chkSelectionElems.length > 1) {
        return;
      }

      const selectedElementChk = chkSelectionElems[0] as HTMLInputElement;

      if (selectedElementChk.dataset.type === 'menubook') {
        const menuBookId = Number(selectedElementChk.value);
        setSelectedMenuBookId(undefined);
        setCopyFromMenuBookId(menuBookId);
        setShowMenuBookDetailModal(true);
      } else if (selectedElementChk.dataset.type === 'course') {
        const [menuBookId, courseId] = selectedElementChk.value.split('-');
        setCourseDetailModalData({
          ...courseDetailModalData,
          menuBookId: Number(menuBookId),
          menuBookName: selectedElementChk.dataset.menubookname || '',
          courseId: undefined,
          copyFromCourseId: Number(courseId),
        });
        setShowCourseDetailModal(true);
      }
    }
  }

  // handle restore course
  async function processRestoreCourse(courseId: number) {
    try {
      const response = await menuBookService.restoreCourse({ ids: [courseId] });
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadMenuListData();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  const handleRestoreCourse = useCallback(function (courseId: number) {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('MenuBookListPage.warn_confirm_restore')}
        onConfirm={processRestoreCourse.bind(null, courseId)}
      />
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle restore menu-book
  async function processRestoreMenuBook(menuBookId: number) {
    try {
      const response = await menuBookService.restoreMenuBook({ ids: [menuBookId] });
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadMenuListData();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  const handleRestoreMenuBook = useCallback(function (menuBookId: number) {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('MenuBookListPage.warn_confirm_restore')}
        onConfirm={processRestoreMenuBook.bind(null, menuBookId)}
      />
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle delete
  function processDelete() {
    const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-menubook-item > input:checked');
    if (chkSelectionElems.length > 0) {
      //compose delete data
      const payload: MenuBookDeletePayload[] = [];

      chkSelectionElems.forEach((element) => {
        if (element.dataset.type === 'menubook') {
          const menuBookId = Number(element.value);
          payload.push({
            menu_book_id: menuBookId,
            course_id: null,
          });
        } else if (element.dataset.type === 'course') {
          const [menuBookId, courseId] = element.value.split('-');
          payload.push({
            menu_book_id: Number(menuBookId),
            course_id: Number(courseId),
          });
        }
      });

      if (payload.length > 0) {
        (async () => {
          try {
            const response = await menuBookService.delete(payload);
            showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
            loadMenuListData();
          } catch (e) {
            if (!handleServiceError(showGlobalModal, e as AxiosError)) {
              displayError(showGlobalModal, e as AxiosError);
            }
          }
        })();
      }
    }
  }

  function handleDelete() {
    const chkSelectionElems = document.querySelectorAll('.chk-menubook-item > input:checked');
    if (chkSelectionElems.length > 0) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('MenuBookListPage.warn_confirm_delete')}
          onConfirm={processDelete}
        />
      ));
    }
  }

  const handleSelectedRowChanged = useCallback(function () {
    if (btnViewDetailRef.current && btnAddCourseRef.current && btnCopyRef.current && btnDeleteRef.current) {
      const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-menubook-item > input:checked');
      if (chkSelectionElems.length === 0) {
        btnViewDetailRef.current.disabled = true;
        btnAddCourseRef.current.disabled = true;
        btnCopyRef.current.disabled = true;
        btnDeleteRef.current.disabled = true;
      } else if (chkSelectionElems.length === 1) {
        const chkSelectionElem = chkSelectionElems[0];
        if (chkSelectionElem.dataset.type === 'menubook') {
          btnViewDetailRef.current.disabled = false;
          btnAddCourseRef.current.disabled = false;
          btnCopyRef.current.disabled = false;
          btnDeleteRef.current.disabled = false;
        } else if (chkSelectionElem.dataset.type === 'course') {
          btnViewDetailRef.current.disabled = false;
          btnAddCourseRef.current.disabled = true;
          btnCopyRef.current.disabled = false;
          btnDeleteRef.current.disabled = false;
        }
      } else {
        btnViewDetailRef.current.disabled = true;
        btnAddCourseRef.current.disabled = true;
        btnCopyRef.current.disabled = true;
        btnDeleteRef.current.disabled = false;
      }
    }
  }, []);

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <FeatureTitleBar title={t('MenuBookListPage.head_title')}></FeatureTitleBar>
          </div>
          <form ref={formSearchRef} className="mt-3 mb-3" onSubmit={handleSubmit}>
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>{t('MenuBookListPage.search')}</Accordion.Header>
                <Accordion.Body onExited={saveFormSearch}>
                  <Row>
                    <Col sm={3}>
                      <Row className="mb-3">
                        <Col sm={'auto'} className="text-end">
                          <Form.Label htmlFor="txt-menu-book-name" className="col-form-label">
                            {t('MenuBookListPage.menu_book_name')}:
                          </Form.Label>
                        </Col>
                        <Col sm={'auto'}>
                          <Form.Control id="txt-menu-book-name" name="menu_book_name" />
                        </Col>
                      </Row>
                    </Col>

                    <Col sm={3}>
                      <Row className="mb-0">
                        <Col sm={'auto'} className="text-end">
                          <Form.Label className="col-form-label">{t('MenuBookListPage.delete')}:</Form.Label>
                        </Col>
                        <Col sm={'auto'}>
                          {Object.keys(SHOW_STATUS_SOURCE).map(function (key) {
                            return (
                              <Form.Check
                                key={`show-status-${key}`}
                                className="col-form-label1 form-label1 py-1"
                                type="radio"
                                name="show_flg"
                                id={`show-status-${key}`}
                                value={key}
                                defaultChecked={key === SEARCH_SHOW_DELETE_KEY_DEFAULT}
                                label={SHOW_STATUS_SOURCE[key]}
                              />
                            );
                          })}
                        </Col>
                      </Row>
                    </Col>

                    <Col sm={5}>
                      <div className="mb-3">
                        <strong>{t('MenuBookListPage.search_detail')}</strong>
                      </div>
                      <Tabs
                        defaultActiveKey={SEARCH_TAB_KEY_1}
                        onSelect={(k) => setSearchDetailTabKey(k)}
                        id="uncontrolled-tab-example"
                        className="mb-3"
                      >
                        <Tab eventKey={SEARCH_TAB_KEY_1} title={t('MenuBookListPage.tab_search_sale_type')}>
                          <div className="px-3">
                            <Row className="mb-0">
                              <Col sm={'auto'} className="text-end">
                                <Form.Label htmlFor="txt-sale-type-id" className="col-form-label">
                                  {t('MenuBookListPage.sale_type')}：
                                </Form.Label>
                              </Col>
                              <Col sm={'auto'}>
                                <Form.Select id="txt-sale-type-id" name="sale_type_id">
                                  <option value="">{t('MenuBookListPage.all_saletype')}</option>
                                  {saleTypeSourceMap &&
                                    Object.keys(saleTypeSourceMap).map((key) => (
                                      <option key={key} value={key}>
                                        {saleTypeSourceMap[Number(key)]}
                                      </option>
                                    ))}
                                </Form.Select>
                              </Col>
                            </Row>
                          </div>
                        </Tab>
                        <Tab eventKey={SEARCH_TAB_KEY_2} title={t('MenuBookListPage.tab_search_store')}>
                          <div className="px-3">
                            <Row>
                              <Col xxl="auto" xl={'auto'} className="text-end">
                                <Form.Label htmlFor="txt-sale-type-id" className="col-form-label">
                                  {t('MenuBookListPage.target_date')}:
                                </Form.Label>
                              </Col>
                              <Col xxl="auto" xl={4}>
                                <ReactDatePicker
                                  locale="ja"
                                  className="ams-month-picker__input"
                                  popperPlacement="bottom"
                                  selected={dataDate}
                                  onChange={handleDateChanged}
                                  dateFormat="yyyy/MM/dd"
                                  customInput={<CustomInput />}
                                />
                              </Col>
                              <Col xxl="auto" xl={'auto'} className="text-end">
                                <Form.Label htmlFor="txt-sale-type-id" className="col-form-label">
                                  {t('MenuBookListPage.store_name')}:
                                </Form.Label>
                              </Col>
                              <Col xxl="auto" xl={4}>
                                <Select<RestaurantOption>
                                  styles={{
                                    container: (provided, state) => ({
                                      ...provided,
                                      padding: '0',
                                      border: '0',
                                      fontSize: '1rem',
                                      width: '200px',
                                    }),
                                    control: (provided, state) => ({
                                      ...provided,
                                      height: '29px',
                                      minHeight: '29px',
                                      fontSize: '1rem',
                                      borderColor: '#d8dbe0',
                                      boxShadow: 'inset 0 1px 1px rgba(0, 0, 0, 0.075)',
                                      transition:
                                        'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                      ':hover': {
                                        borderColor: state.isFocused ? '#9ba4cd' : '#ced4da',
                                        boxShadow: state.isFocused
                                          ? 'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px rgba(102, 175, 233, 0.6)'
                                          : 'inset 0 1px 1px rgba(0, 0, 0, 0.075)',
                                      },
                                    }),
                                    valueContainer: (provided, state) => ({
                                      ...provided,
                                      marginTop: '0',
                                      marginLeft: '6px',
                                      padding: '0',
                                      border: '0',
                                    }),
                                    dropdownIndicator: (provided, state) => ({
                                      ...provided,
                                      marginTop: '0',
                                      padding: '4px',
                                      border: '0',
                                      width: '24px',
                                    }),
                                    clearIndicator: (provided, state) => ({
                                      ...provided,
                                      marginTop: '0',
                                      padding: '0',
                                      border: '0',
                                      width: '16px',
                                    }),
                                    indicatorsContainer: (provided, state) => ({
                                      ...provided,
                                      paddingRight: '0px',
                                      border: '0',
                                    }),
                                  }}
                                  defaultValue={restaurantOptionListSource[0]}
                                  value={searchRestaurantOption}
                                  options={restaurantOptionListSource}
                                  onChange={handleSelectDepartmentChange}
                                  isSearchable
                                ></Select>
                              </Col>
                            </Row>
                          </div>
                        </Tab>
                      </Tabs>
                    </Col>
                    <Col sm={1}>
                      <Button type="submit">{t('MenuBookListPage.search')}</Button>
                    </Col>
                  </Row>
                </Accordion.Body>
                <div className="accordion-body accordion-summary">
                  <Row>
                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('MenuBookListPage.menu_book_name')}：</Form.Label>
                      <Form.Label className="col-form-label">{formSearchValue.menuBookName || '-'}</Form.Label>
                    </Col>

                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('MenuBookListPage.delete')}：</Form.Label>
                      <Form.Label className="col-form-label">
                        {SHOW_STATUS_SOURCE[formSearchValue.showFlg] || '-'}
                      </Form.Label>
                    </Col>

                    {searchDetailTabKey === SEARCH_TAB_KEY_1 && (
                      <Col sm={'auto'}>
                        <Form.Label className="col-form-label">{t('MenuBookListPage.sale_type')}：</Form.Label>
                        <Form.Label className="col-form-label">
                          {(saleTypeSourceMap && saleTypeSourceMap[Number(formSearchValue.saleType)]) || '-'}
                        </Form.Label>
                      </Col>
                    )}

                    {searchDetailTabKey === SEARCH_TAB_KEY_2 && (
                      <Col sm={'auto'}>
                        <Form.Label className="col-form-label">{t('MenuBookListPage.target_date')}：</Form.Label>
                        <Form.Label className="col-form-label">
                          {moment(dataDate).format(DATE_FORMAT_YYYY_MM_DD)}
                        </Form.Label>
                      </Col>
                    )}

                    {searchDetailTabKey === SEARCH_TAB_KEY_2 && (
                      <Col sm={'auto'}>
                        <Form.Label className="col-form-label">{t('MenuBookListPage.store_name')}：</Form.Label>
                        <Form.Label className="col-form-label">
                          {formSearchValue.restaurantOption?.label || '-'}
                        </Form.Label>
                      </Col>
                    )}
                  </Row>
                </div>
              </Accordion.Item>
            </Accordion>
          </form>

          <div className="text-end mb-3">
            <Button className="btn--dark-green ms-3" onClick={handleOpenMenuBookDetailModal}>
              {t('MenuBookListPage.add_menu_book')}
            </Button>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
            <div>
              <div className="d-inline-block me-4">
                <Button
                  variant="link"
                  className="px-0 text-decoration-none"
                  onClick={handleSelectAll}
                  style={{ minWidth: 'auto' }}
                >
                  {t('SelectMenuBook.select_all')}
                </Button>
                <span>／</span>
                <Button
                  variant="link"
                  className="px-0 text-decoration-none"
                  onClick={handleUnSelectAll}
                  style={{ minWidth: 'auto' }}
                >
                  {t('SelectMenuBook.unselect_all')}
                </Button>
              </div>
            </div>
            <div>
              <Button ref={btnViewDetailRef} className="ms-3" onClick={handleOpenMenuBookOrCourseDetailModal}>
                {t('MenuBookListPage.detail')}
              </Button>
              <Button ref={btnAddCourseRef} className="ms-3" onClick={handleOpenCourseDetailModal}>
                {t('MenuBookListPage.add_course')}
              </Button>
              <Button ref={btnCopyRef} className="ms-3" onClick={handleCopyMenuBookOrCourseDetailModal}>
                {t('MenuBookListPage.copy')}
              </Button>
              <Button ref={btnDeleteRef} className="btn--red ms-3" onClick={handleDelete}>
                {t('MenuBookListPage.delete')}
              </Button>
            </div>
          </div>

          <MenuBookListTable
            menuBookList={menuBookList}
            onSortMenuBook={handleSortMenuBook}
            onSortCourse={handleSortCourse}
            onRestoreMenuBook={handleRestoreMenuBook}
            onRestoreCourse={handleRestoreCourse}
            onSelectedRowChanged={handleSelectedRowChanged}
            onOpenEditMenuBookModal={handleOpenEditMenuBookModal}
          />

          {showEditMenuBookModal &&
            ReactDOM.createPortal(
              <MenuBookEditorModal
                menuBookId={menuBookEditorData?.menuBookId}
                menuBookName={menuBookEditorData?.menuBookName}
                courseId={menuBookEditorData?.courseId}
                courseName={menuBookEditorData?.courseName}
                onClose={handleCloseEditMenuBookModal}
              />,
              document.body
            )}

          {showMenuBookDetailModal &&
            ReactDOM.createPortal(
              <MenuBookDetailModal
                menuBookId={selectedMenuBookId}
                copyFromMenuBookId={copyFromMenuBookId}
                onClose={handleCloseMenuBookDetailModal}
              />,
              document.body
            )}

          {showCourseDetailModal &&
            courseDetailModalData &&
            ReactDOM.createPortal(
              <CourseDetailModal
                menuBookId={courseDetailModalData.menuBookId}
                menuBookName={courseDetailModalData.menuBookName}
                courseId={courseDetailModalData.courseId}
                copyFromCourseId={courseDetailModalData.copyFromCourseId}
                onClose={handleCloseCourseDetailModal}
              />,
              document.body
            )}
        </div>
      </div>
    </div>
  );
}

export default MenuBookListPage;
